import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDate } from "../lib/utils-app";
import { articleRegion } from "../lib/article-utils";
import Seo from "../components/Seo";
import Img from "../components/Img";
import { Mail, Sparkles, ShieldCheck, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

export default function Digest() {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/digest").then((r) => setItems(r.data.items)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("Subscribed. Friday's digest is on its way.");
      setEmail("");
    } catch { toast.error("Subscribe failed — please retry."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="digest-page">
      <Seo
        title="Weekly Arbitration Digest"
        description="Every Friday — a curated roundup of arbitration, ODR and enforcement developments from India and the world. Free, AI-assisted, human-reviewed."
        path="/digest"
      />
      <section className="intel-grid border-b border-[var(--io-border)]">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow" style={{ color: "#D4AF37" }}>Every Friday</div>
            <h1 className="font-serif text-[44px] md:text-[60px] mt-2 leading-[1.03] text-[var(--io-text)]">The Weekly Arbitration Digest</h1>
            <p className="mt-5 text-[17px] text-[var(--io-text-2)] max-w-[54ch]">
              A curated roundup of arbitration, ODR and enforcement developments — India and global,
              institutions and courts, all decoded in ten minutes.
            </p>
            <div className="mt-5 flex gap-2">
              <span className="io-badge io-badge-blue"><Sparkles size={11}/> AI-assisted</span>
              <span className="io-badge io-badge-green"><ShieldCheck size={11}/> Human reviewed</span>
              <span className="io-badge io-badge-gold"><FileCheck2 size={11}/> Source-backed</span>
            </div>
          </div>

          <form onSubmit={subscribe} className="io-card-el p-6 md:p-8" data-testid="digest-subscribe-form">
            <div className="eyebrow">Subscribe (Free)</div>
            <h3 className="font-serif text-[24px] mt-2 text-[var(--io-text)]">Join 200+ GCs &amp; arbitrators</h3>
            <div className="mt-5 flex items-center gap-3 border border-[var(--io-border)] focus-within:border-[var(--io-gold)] rounded-md px-3 bg-[var(--io-bg-2)]">
              <Mail size={16} className="text-[var(--io-muted)]"/>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@firm.com" data-testid="digest-email"
                className="flex-1 bg-transparent py-3 text-[14px] outline-none text-[var(--io-text)] placeholder:text-[var(--io-muted)]"/>
            </div>
            <button disabled={submitting} className="btn btn-primary w-full justify-center mt-3" data-testid="digest-submit">
              {submitting ? "Subscribing…" : "Subscribe to the digest"}
            </button>
            <p className="mt-3 text-[11px] text-[var(--io-muted)]">Unsubscribe anytime. We never share your email.</p>
          </form>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <h2 className="font-serif text-[28px] md:text-[36px] text-[var(--io-text)] mb-8">Past editions</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((a) => (
            <Link key={a.id} to={`/article/${a.slug}`} className="block io-card overflow-hidden group" data-testid={`digest-${a.slug}`}>
              {a.hero_image && <div className="aspect-[16/9] overflow-hidden border-b border-[var(--io-border)]"><Img src={a.hero_image} className="w-full h-full object-cover img-hover" w={600}/></div>}
              <div className="p-5">
                <div className="font-mono text-[11px] text-[var(--io-muted)]"><span className="text-[var(--io-gold)]">Weekly Digest</span> · {articleRegion(a)} · {fmtDate(a.published_at)}</div>
                <h3 className="font-serif text-[22px] mt-2 text-[var(--io-text)] group-hover:text-[var(--io-gold)]">{a.headline}</h3>
                <p className="mt-2 text-[14px] text-[var(--io-text-2)] line-clamp-3">{a.summary}</p>
              </div>
            </Link>
          ))}
          {items.length === 0 && <div className="text-[var(--io-muted)]">No digest entries yet.</div>}
        </div>
      </div>
    </div>
  );
}
